import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getTerms, getTermVersionDetail } from '../api/terms';
import styles from './TermsModal.module.css';

// 표는 GFM 확장이라 플러그인 없이는 파싱되지 않고 문단으로 뭉개진다.
// 좁은 화면에서 표가 시트를 가로로 밀지 않도록 스크롤 상자로 감싼다.
const MD_COMPONENTS = {
  table: (props) => <div className={styles.tableScroll}><table {...props} /></div>,
};

/**
 * 약관 전문.
 * 모바일은 바텀시트, 데스크톱은 중앙 다이얼로그로 뜬다 —
 * 넓은 화면에서 시트로 깔면 한 줄이 1400px까지 늘어나 읽을 수 없다.
 *
 * @param {() => void} [onAgree]  주면 하단에 '동의합니다' 버튼이 붙는다.
 *                                읽고 바로 동의로 이어져 체크박스를 다시 찾을 필요가 없다.
 */
export default function TermsModal({ type, onClose, onAgree }) {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const sheetRef = useRef(null);
  const lastFocused = useRef(null);

  useEffect(() => {
    lastFocused.current = document.activeElement;
    sheetRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      // 긴 스크롤 콘텐츠라 포커스가 새면 뒤쪽 회원가입 폼으로 넘어간다
      const items = sheetRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!items?.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      lastFocused.current?.focus?.();
    };
  }, [onClose]);

  useEffect(() => {
    const apiType = type === 'terms' ? 'TERMS' : 'PRIVACY';
    getTerms()
      .then((list) => {
        const term = (list ?? []).find((t) => t.type === apiType);
        if (!term) return;
        setTitle(term.name ?? '');
        // 현재 버전 바로 사용 (versionId가 있으면 상세 조회, 없으면 content 직접 사용)
        if (term.versionId) {
          return getTermVersionDetail(term.termId, term.versionId).then((detail) => {
            setContent(detail?.content ?? '');
          });
        } else if (term.content) {
          setContent(term.content);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type]);

  const displayTitle = title || (type === 'terms' ? '서비스 이용약관' : '개인정보처리방침');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.sheet}
        ref={sheetRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.handle} />

        <div className={styles.header}>
          <span className={styles.title} id="terms-modal-title">{displayTitle}</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label="닫기">
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <p className={styles.loading}>불러오는 중…</p>
          ) : content ? (
            <div className={styles.mdContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                {content}
              </ReactMarkdown>
            </div>
          ) : (
            <p className={styles.loading}>내용을 불러올 수 없습니다</p>
          )}
        </div>

        {onAgree && (
          <div className={styles.footer}>
            <button className={styles.agreeBtn} onClick={onAgree}>동의합니다</button>
          </div>
        )}
      </div>
    </div>
  );
}
